import { io, Socket } from 'socket.io-client';
import {
  SignalingEvents,
  UserRole,
  RemoteCommandPayload,
  ClinicalAidPayload,
  WhiteboardPayload,
  ChatMessage,
  GpsCoordinates,
  TranscriptionResult
} from '@sos/shared';

export interface DispatcherSignalingHandlers {
  onOffer?: (data: { offer: RTCSessionDescriptionInit; fromPeerId: string }) => void;
  onAnswer?: (data: { answer: RTCSessionDescriptionInit; fromPeerId: string }) => void;
  onIceCandidate?: (data: { candidate: RTCIceCandidateInit; fromPeerId: string }) => void;
  onPeerConnected?: (data: { peerId: string; role: string; clientMetadata?: Record<string, unknown> }) => void;
  onPeerDisconnected?: (data: { peerId: string; role: string }) => void;
  onGpsUpdate?: (coords: GpsCoordinates) => void;
  onChatMessage?: (msg: ChatMessage) => void;
  onTranscriptionUpdate?: (result: TranscriptionResult) => void;
  onCallEnded?: () => void;
  onError?: (err: { code: string; message: string }) => void;
}

export class DispatcherSignalingService {
  private socket: Socket | null = null;
  private isConnected: boolean = false;

  constructor(private readonly serverUrl: string) {}

  public getIsConnected(): boolean {
    return this.isConnected;
  }

  public connect(
    sessionId: string,
    token: string,
    handlers: DispatcherSignalingHandlers
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.socket = io(this.serverUrl, {
        transports: ['websocket', 'polling'],
        reconnection: true
      });

      this.socket.on('connect', () => {
        this.isConnected = true;

        this.socket?.emit(
          SignalingEvents.JOIN_ROOM,
          {
            sessionId,
            token,
            role: UserRole.DISPATCHER,
            clientMetadata: {
              userAgent: navigator.userAgent
            }
          },
          (res: { success: boolean; error?: string }) => {
            if (res?.success) {
              resolve();
            } else {
              reject(new Error(res?.error || 'Error al conectar a la sesión como despachador'));
            }
          }
        );
      });

      this.socket.on('connect_error', (err) => {
        reject(err);
      });

      // Mapear eventos entrantes
      this.socket.on(SignalingEvents.SDP_OFFER, (data) => handlers.onOffer?.(data));
      this.socket.on(SignalingEvents.SDP_ANSWER, (data) => handlers.onAnswer?.(data));
      this.socket.on(SignalingEvents.ICE_CANDIDATE, (data) => handlers.onIceCandidate?.(data));
      this.socket.on(SignalingEvents.PEER_CONNECTED, (data) => handlers.onPeerConnected?.(data));
      this.socket.on(SignalingEvents.PEER_DISCONNECTED, (data) => handlers.onPeerDisconnected?.(data));
      this.socket.on(SignalingEvents.GPS_UPDATE, (coords) => handlers.onGpsUpdate?.(coords));
      this.socket.on(SignalingEvents.CHAT_MESSAGE, (msg) => handlers.onChatMessage?.(msg));
      this.socket.on(SignalingEvents.TRANSCRIPTION_UPDATE, (data) => handlers.onTranscriptionUpdate?.(data));
      this.socket.on(SignalingEvents.CALL_ENDED, () => handlers.onCallEnded?.());
      this.socket.on(SignalingEvents.SIGNALING_ERROR, (err) => handlers.onError?.(err));
    });
  }

  public sendOffer(offer: RTCSessionDescriptionInit, toPeerId?: string): void {
    if (toPeerId) {
      this.socket?.emit(SignalingEvents.SDP_OFFER, { toPeerId, offer });
    } else {
      this.socket?.emit(SignalingEvents.SDP_OFFER, offer);
    }
  }

  public sendAnswer(answer: RTCSessionDescriptionInit, toPeerId?: string): void {
    if (toPeerId) {
      this.socket?.emit(SignalingEvents.SDP_ANSWER, { toPeerId, answer });
    } else {
      this.socket?.emit(SignalingEvents.SDP_ANSWER, answer);
    }
  }

  public sendIceCandidate(candidate: RTCIceCandidateInit, toPeerId?: string): void {
    if (toPeerId) {
      this.socket?.emit(SignalingEvents.ICE_CANDIDATE, { toPeerId, candidate });
    } else {
      this.socket?.emit(SignalingEvents.ICE_CANDIDATE, candidate);
    }
  }

  public sendRemoteCommand(cmd: RemoteCommandPayload): void {
    this.socket?.emit(SignalingEvents.REMOTE_COMMAND, cmd);
  }

  public sendClinicalAid(aid: ClinicalAidPayload): void {
    this.socket?.emit(SignalingEvents.CLINICAL_AID, aid);
  }

  public sendWhiteboardUpdate(payload: WhiteboardPayload): void {
    this.socket?.emit(SignalingEvents.WHITEBOARD_UPDATE, payload);
  }

  public sendChatMessage(message: string): void {
    this.socket?.emit(SignalingEvents.CHAT_MESSAGE, { message });
  }

  public sendTranscriptionUpdate(result: TranscriptionResult): void {
    this.socket?.emit(SignalingEvents.TRANSCRIPTION_UPDATE, result);
  }

  public endCall(reason?: string): void {
    this.socket?.emit(SignalingEvents.CALL_ENDED, { reason });
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }
}
